/* VALM CUSTOM: Read the public Shopify sitemap index so new collections, products, pages and blogs need no manual link maintenance. */
(() => {
  const resources = new Set(['collections', 'products', 'pages', 'blogs', 'policies']);
  const mapPath = /^\/sitemap_(products|pages|collections|blogs)(?:_\d+)?\.xml$/;
  class ValmSitemap extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.controller = new AbortController();
      this.groups = new Map([...this.querySelectorAll('[data-group]')].map(group => [group.dataset.group, { element: group, links: new Map() }]));
      this.status = this.querySelector('[role="status"]');
      this.search = this.querySelector('input[type="search"]');
      for (const [key, group] of this.groups) {
        for (const link of group.element.querySelectorAll('.valm-sitemap__links a')) {
          const path = this.localPath(link.href);
          if (path) group.links.set(path, { path, title: link.textContent.trim() });
        }
      }
      this.search.addEventListener('input', () => this.filter(), { signal: this.controller.signal });
      this.querySelector('.valm-sitemap__search').hidden = false;
      this.updateCounts();
      this.load();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.started = false;
    }
    localPath(value) {
      try {
        const url = new URL(value, location.origin);
        const primary = new URL(this.dataset.shopOrigin, location.origin);
        if (!['https:', 'http:'].includes(url.protocol) || ![location.origin, primary.origin].includes(url.origin)) return null;
        return url.pathname.replace(/\/$/, '') || '/';
      } catch { return null; }
    }
    classify(path) {
      if (path === '/') return 'pages';
      const parts = path.split('/').filter(Boolean);
      const index = parts.findIndex((part, i) => i < 2 && resources.has(part));
      if (index < 0 || parts.length <= index + 1) return null;
      if (parts[index] === 'blogs') return parts.length > index + 2 ? 'articles' : 'blogs';
      return parts[index];
    }
    title(path) {
      if (path === '/') return this.dataset.homeLabel;
      let handle = path.split('/').filter(Boolean).at(-1);
      try { handle = decodeURIComponent(handle); } catch { /* Retain a readable encoded handle. */ }
      return handle.replace(/[-_]+/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());
    }
    async readXml(url) {
      const response = await fetch(url, { signal: this.controller.signal, cache: 'no-cache', credentials: 'same-origin' });
      if (!response.ok) throw new Error('Sitemap unavailable');
      const xml = new DOMParser().parseFromString(await response.text(), 'application/xml');
      if (xml.querySelector('parsererror')) throw new Error('Invalid sitemap');
      const root = xml.documentElement;
      if (!['sitemapindex', 'urlset'].includes(root.localName)) throw new Error('Invalid sitemap root');
      return { type: root.localName, urls: [...root.children].map(entry => [...entry.children].find(child => child.localName === 'loc')?.textContent.trim()).filter(Boolean) };
    }
    async load() {
      const timeout = setTimeout(() => this.controller.abort(), 15000);
      try {
        const index = await this.readXml('/sitemap.xml');
        let urls = index.urls;
        if (index.type === 'sitemapindex') {
          const maps = [...new Set(index.urls.map(url => this.localPath(url)).filter(path => path && mapPath.test(path)))];
          if (!maps.length) throw new Error('No storefront sitemaps');
          // Keep sitemap query bounds while allowing only local, known resource maps.
          const queue = index.urls.filter(url => maps.includes(this.localPath(url)));
          const chunks = [];
          let next = 0;
          await Promise.all(Array.from({ length: Math.min(4, queue.length) }, async () => {
            while (next < queue.length) {
              const url = new URL(queue[next++], location.origin);
              chunks.push((await this.readXml(url.pathname + url.search)).urls);
            }
          }));
          urls = chunks.flat();
        }
        for (const url of urls) {
          const path = this.localPath(url);
          const key = path && this.classify(path);
          const group = this.groups.get(key);
          if (group && !group.links.has(path)) group.links.set(path, { path, title: this.title(path) });
        }
        this.render();
        this.dataset.ready = 'true';
      } catch (error) {
        if (this.isConnected) this.status.textContent = this.dataset.errorLabel;
      } finally { clearTimeout(timeout); }
    }
    render() {
      const collator = new Intl.Collator(document.documentElement.lang || 'en', { numeric: true, sensitivity: 'base' });
      for (const group of this.groups.values()) {
        const fragment = document.createDocumentFragment();
        for (const { path, title } of [...group.links.values()].sort((a, b) => collator.compare(a.title, b.title))) {
          const item = document.createElement('li');
          const link = document.createElement('a');
          const label = document.createElement('span');
          link.className = 'reversed-link'; link.href = path;
          label.className = 'reversed-link__text'; label.textContent = title;
          link.append(label); item.append(link); fragment.append(item);
        }
        group.element.querySelector('.valm-sitemap__links').replaceChildren(fragment);
        for (const pagination of group.element.querySelectorAll('.valm-sitemap__pagination')) pagination.hidden = true;
      }
      this.filter();
    }
    updateCounts() {
      for (const [key, group] of this.groups) {
        const count = [...group.element.querySelectorAll('.valm-sitemap__links li')].filter(item => !item.hidden).length;
        group.element.querySelector('.valm-sitemap__count').textContent = `(${count})`;
        group.element.hidden = count === 0;
        this.querySelector(`[data-jump="${key}"]`).hidden = count === 0;
      }
    }
    filter() {
      const query = this.search.value.trim().toLocaleLowerCase();
      let count = 0;
      for (const group of this.groups.values()) {
        for (const item of group.element.querySelectorAll('.valm-sitemap__links li')) {
          item.hidden = query !== '' && !item.textContent.toLocaleLowerCase().includes(query);
          if (!item.hidden) count++;
        }
      }
      this.updateCounts();
      this.status.textContent = query ? this.dataset.resultsLabel.replace('[count]', count) : '';
    }
  }
  if (!customElements.get('valm-sitemap')) customElements.define('valm-sitemap', ValmSitemap);
})();
